import { Amplify } from 'aws-amplify'
import {
  fetchAuthSession,
  signInWithRedirect,
  signOut,
  getCurrentUser,
  signIn as cognitoSignIn,
  signUp as cognitoSignUp,
  confirmSignUp as cognitoConfirmSignUp,
  resendSignUpCode as cognitoResendSignUpCode,
  resetPassword as cognitoResetPassword,
  confirmResetPassword
} from 'aws-amplify/auth'
import { registerAuthProvider, assertProviderConfig } from './auth'
import { splitSignupMetadata } from './cognito-signup-metadata'
import type { AuthClient, AuthProviderContext } from './types'

/** Cognito auth provider factory — pass to createFoundation({ auth: cognitoAuth }) or import to auto-register */
export const cognitoAuth = async (config: Record<string, unknown>, _ctx: AuthProviderContext): Promise<AuthClient> => {
  const cognito = assertProviderConfig(config, 'cognito', ['userPoolId', 'clientId', 'domain']) as
    { userPoolId: string; clientId: string; region: string; domain: string; scope?: string }

  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: cognito.userPoolId,
        userPoolClientId: cognito.clientId,
        loginWith: {
          oauth: {
            domain: cognito.domain.replace('https://', ''),
            scopes: (cognito.scope || 'openid profile email').split(' '),
            redirectSignIn: [window.location.origin],
            redirectSignOut: [window.location.origin],
            responseType: 'code'
          }
        }
      }
    }
  })

  return {
    login: async () => { await signInWithRedirect() },
    logout: async () => { await signOut() },
    getUser: async () => {
      try {
        const user = await getCurrentUser()

        // ID token claims are the authoritative profile source: signInDetails
        // only exists for password sign-ins (absent after a hosted-UI redirect),
        // and in email-alias pools `username` is the generated sub — an
        // identifier, not a display name.
        let claims: Record<string, unknown> = {}
        try {
          const session = await fetchAuthSession()
          claims = (session.tokens?.idToken?.payload ?? {}) as Record<string, unknown>
        } catch { /* no readable session — fall back to getCurrentUser fields */ }

        const claimString = (key: string): string =>
          typeof claims[key] === 'string' ? (claims[key] as string).trim() : ''

        const email = claimString('email') || user.signInDetails?.loginId || ''
        const fullName = [claimString('given_name'), claimString('family_name')]
          .filter(Boolean)
          .join(' ')
        const usernameIsHandle = user.username !== '' && user.username !== user.userId
        const name =
          claimString('name') ||
          fullName ||
          (usernameIsHandle ? user.username : undefined)

        return { id: user.userId, email, name }
      } catch { return undefined }
    },
    getTokenSilently: async () => {
      const session = await fetchAuthSession()
      const token = session.tokens?.accessToken?.toString()
      if (!token) throw new Error('No token available')
      return token
    },
    isAuthenticated: async () => {
      try {
        await getCurrentUser()
        return true
      } catch { return false }
    },

    async handleCallback() {
      // Amplify handles the callback automatically when configured with OAuth
      // Just ensure the session is refreshed
      await fetchAuthSession({ forceRefresh: true })
    },

    async signIn(email: string, password: string) {
      const result = await cognitoSignIn({ username: email, password })
      return {
        isSignedIn: result.isSignedIn,
        nextStep: result.nextStep as { signInStep: string; [key: string]: unknown } | undefined
      }
    },

    async signUp(email: string, password: string, metadata?: Record<string, unknown>) {
      const { userAttributes, clientMetadata } = splitSignupMetadata(metadata)
      const result = await cognitoSignUp({
        username: email,
        password,
        options: {
          userAttributes: { email, ...userAttributes },
          ...(Object.keys(clientMetadata).length ? { clientMetadata } : {})
        }
      })
      return {
        isSignUpComplete: result.isSignUpComplete,
        userId: result.userId,
        nextStep: result.nextStep as { signUpStep: string; [key: string]: unknown } | undefined
      }
    },

    async confirmSignUp(email: string, code: string) {
      await cognitoConfirmSignUp({ username: email, confirmationCode: code })
    },

    async resendSignUpCode(email: string) {
      await cognitoResendSignUpCode({ username: email })
    },

    async forgotPassword(email: string) {
      await cognitoResetPassword({ username: email })
    },

    async resetPassword(email: string, code: string, newPassword: string) {
      await confirmResetPassword({ username: email, confirmationCode: code, newPassword })
    }
  }
}

// Auto-register when imported
registerAuthProvider('cognito', cognitoAuth)
